/*
 * El Arquero — PostHog custom storefront events.
 *
 * The PostHog snippet in layout/theme.liquid initialises the SDK (through the
 * managed reverse proxy) and drops a `window.ElArqueroAnalytics.context` object
 * describing the current page. This file turns that context plus storefront
 * interactions into named events, and links discount codes to the person
 * profile so a code can be attributed to a customer in PostHog.
 *
 * Events captured:
 *   product_viewed, product_variant_selected, product_media_viewed
 *   collection_viewed, collection_filtered, collection_sorted
 *   search_performed
 *   cart_add, cart_remove, cart_quantity_changed, cart_viewed, cart_drawer_opened
 *   checkout_button_clicked (checkout itself is covered by docs/posthog-web-pixel.js)
 *   discount_link_landed, discount_code_applied, discount_code_removed
 *   order_viewed (order_completed comes from the pixel, at checkout)
 *   newsletter_signup, contact_form_submitted, account_created
 *   video_played, cocktail_recipe_viewed, gift_box_configured
 *   stockist_searched, stockist_store_selected
 *   outbound_link_clicked
 *   storefront_error (plus posthog.captureException for error tracking)
 *
 * Session replay, heatmaps, web vitals and unhandled-exception capture are
 * turned on in the posthog.init() config in layout/theme.liquid; this file adds
 * the errors the theme swallows on its own (failed cart requests, rejected
 * add-to-cart) and tags them with the session replay URL.
 */
(function () {
  'use strict';

  var A = (window.ElArqueroAnalytics = window.ElArqueroAnalytics || {});
  var ctx = A.context || {};
  var customer = A.customer || null;

  var STORAGE_KEY = 'ea_ph_discounts';
  var SEEN_ORDERS_KEY = 'ea_ph_orders_seen';
  var BRIDGE_KEY = 'ea_ph_checkout_bridge';

  /* ------------------------------------------------------------------ utils */

  function noop() {}

  function ph() {
    return window.posthog && typeof window.posthog.capture === 'function' ? window.posthog : null;
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      if (!source) continue;
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key];
      }
    }
    return target;
  }

  function baseProperties() {
    return {
      shopify_template: ctx.template || null,
      shopify_page_type: ctx.page_type || null,
      currency: ctx.currency || null,
      country: ctx.country || null,
      is_logged_in: !!customer,
    };
  }

  function capture(event, properties) {
    var p = ph();
    if (!p) return;
    try {
      p.capture(event, assign(baseProperties(), properties || {}));
    } catch (e) {
      /* analytics must never break the storefront */
    }
  }

  /**
   * Unhandled errors and rejections are already picked up by the SDK's
   * `capture_exceptions`. This is for errors the theme swallows itself —
   * a failed cart request, a rejected product form — which would otherwise
   * never surface. The replay URL is attached so an exception in PostHog
   * links straight to the session that produced it.
   */
  function reportError(error, context) {
    var p = ph();
    if (!p) return;
    try {
      var replayUrl =
        typeof p.get_session_replay_url === 'function' ? p.get_session_replay_url({ withTimestamp: true }) : null;

      if (typeof p.captureException === 'function') {
        p.captureException(error instanceof Error ? error : new Error(String(error)), assign({}, context || {}));
      }

      capture('storefront_error', assign({ message: String((error && error.message) || error) }, context || {}, {
        session_replay_url: replayUrl,
      }));
    } catch (e) {
      /* no-op */
    }
  }

  /** Runs a handler without ever letting it break the storefront, reporting failures. */
  function guard(name, fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        reportError(error, { error_source: 'posthog-events.js', handler: name });
      }
    };
  }

  function setPerson(properties, setOnceProperties) {
    var p = ph();
    if (!p || typeof p.setPersonProperties !== 'function') return;
    try {
      p.setPersonProperties(properties || {}, setOnceProperties || {});
    } catch (e) {
      /* no-op */
    }
  }

  function readStore(key, fallback) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota — the events still fire, we just lose dedupe */
    }
  }

  function money(cents) {
    return typeof cents === 'number' ? Math.round(cents) / 100 : null;
  }

  function param(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function closest(element, selector) {
    if (!element || typeof element.closest !== 'function') return null;
    return element.closest(selector);
  }

  function text(element) {
    return element && element.textContent ? element.textContent.trim().replace(/\s+/g, ' ') : null;
  }

  /* -------------------------------------------------------------- identify */

  function identifyCustomer() {
    var p = ph();
    if (!p || !customer || !customer.id) return;

    try {
      p.identify(
        'shopify_customer_' + customer.id,
        {
          email: customer.email,
          $email: customer.email,
          name: [customer.first_name, customer.last_name].filter(Boolean).join(' ') || null,
          shopify_customer_id: customer.id,
          shopify_orders_count: customer.orders_count,
          shopify_total_spent: customer.total_spent,
          accepts_marketing: customer.accepts_marketing,
          shopify_tags: customer.tags || [],
        },
        {
          first_seen_template: ctx.template || null,
        }
      );
    } catch (e) {
      /* no-op */
    }
  }

  /* -------------------------------------------------------------- discounts */

  /**
   * Discount codes are the one storefront signal that ties a marketing campaign
   * to a person, so every code we see is written to the person profile:
   *   first_discount_code / last_discount_code  — attribution
   *   discount_codes_used[]                     — full history, deduped
   * They are also registered as super properties so every later event in the
   * session carries the active code.
   */
  function discountState() {
    var stored = readStore(STORAGE_KEY, null);
    if (!stored || typeof stored !== 'object') stored = {};
    if (!Array.isArray(stored.codes)) stored.codes = [];
    if (!Array.isArray(stored.active)) stored.active = [];
    return stored;
  }

  function recordDiscountCodes(codes, source, extraProperties) {
    if (!codes || !codes.length) return;

    var state = discountState();
    var known = state.codes.slice();
    var isNew = false;

    codes.forEach(function (code) {
      if (!code) return;
      var normalised = String(code).toUpperCase();
      if (known.indexOf(normalised) === -1) {
        known.push(normalised);
        isNew = true;
      }

      capture(
        'discount_code_applied',
        assign(
          {
            discount_code: normalised,
            discount_source: source,
            is_first_use_of_code: isNew,
          },
          extraProperties || {}
        )
      );
    });

    var latest = String(codes[codes.length - 1]).toUpperCase();

    setPerson(
      {
        last_discount_code: latest,
        discount_codes_used: known,
        discount_codes_used_count: known.length,
        last_discount_code_source: source,
      },
      {
        first_discount_code: latest,
        first_discount_code_source: source,
      }
    );

    var p = ph();
    if (p && typeof p.register === 'function') {
      try {
        p.register({ active_discount_code: latest });
      } catch (e) {
        /* no-op */
      }
    }

    state.codes = known;
    // `active` mirrors what the cart actually holds; a discount *link* does not
    // become an applied cart code until checkout, so it must not touch it or the
    // next cart sync would report a phantom removal.
    if (source !== 'discount_link') {
      state.active = codes.map(function (code) {
        return String(code).toUpperCase();
      });
    }
    writeStore(STORAGE_KEY, state);
  }

  function syncCartDiscounts(cart, source) {
    if (!cart) return;

    var codes = (cart.discount_codes || [])
      .filter(function (entry) {
        return entry && entry.code && entry.applicable !== false;
      })
      .map(function (entry) {
        return String(entry.code).toUpperCase();
      });

    var state = discountState();
    var previouslyActive = state.active || [];

    var added = codes.filter(function (code) {
      return previouslyActive.indexOf(code) === -1;
    });

    var removed = previouslyActive.filter(function (code) {
      return codes.indexOf(code) === -1;
    });

    if (added.length) {
      recordDiscountCodes(added, source || 'cart', {
        cart_total: money(cart.total_price),
        cart_item_count: cart.item_count,
        cart_discount_savings: money(cart.total_discount),
      });
    }

    removed.forEach(function (code) {
      capture('discount_code_removed', { discount_code: code, discount_source: source || 'cart' });
    });

    if (added.length || removed.length) {
      // Re-read: recordDiscountCodes above has already persisted the code history.
      var current = discountState();
      current.active = codes;
      writeStore(STORAGE_KEY, current);

      var p = ph();
      if (p && typeof p.register === 'function') {
        try {
          p.register({ active_discount_code: codes.length ? codes[codes.length - 1] : null });
        } catch (e) {
          /* no-op */
        }
      }
    }
  }

  /** Shopify shares discount links as /discount/CODE or ?discount=CODE. */
  function captureDiscountLanding() {
    var fromQuery = param('discount');
    var fromPath = (window.location.pathname.match(/^\/discount\/([^/?#]+)/) || [])[1];
    var code = fromQuery || (fromPath ? decodeURIComponent(fromPath) : null);
    if (!code) return;

    capture('discount_link_landed', {
      discount_code: String(code).toUpperCase(),
      landing_path: window.location.pathname,
      referrer: document.referrer || null,
    });

    recordDiscountCodes([code], 'discount_link', { landing_path: window.location.pathname });
  }

  /* --------------------------------------------------- checkout identity bridge */

  /**
   * Shopify's checkout is not themeable, so it is instrumented by the custom
   * web pixel in docs/posthog-web-pixel.js. That pixel runs in a sandboxed
   * iframe with its own storage, so it cannot read this page's PostHog IDs and
   * would otherwise report checkout as a brand new anonymous person.
   *
   * Cart attributes survive the hand-off to checkout and are readable from the
   * pixel, so the distinct ID and session ID are written there. Keys are
   * underscore-prefixed, which keeps them hidden from the customer and from the
   * order detail screen in admin.
   */
  function cartRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root + path;
  }

  function whenPostHogLoaded(callback, attemptsLeft) {
    var p = window.posthog;
    if (p && p.__loaded && typeof p.get_distinct_id === 'function') {
      callback(p);
      return;
    }
    if ((attemptsLeft === undefined ? 20 : attemptsLeft) <= 0) return;
    window.setTimeout(function () {
      whenPostHogLoaded(callback, (attemptsLeft === undefined ? 20 : attemptsLeft) - 1);
    }, 300);
  }

  function bridgeIdentityToCheckout(options) {
    if (!window.fetch) return;

    whenPostHogLoaded(function (p) {
      var distinctId;
      var sessionId;

      try {
        distinctId = p.get_distinct_id();
        sessionId = typeof p.get_session_id === 'function' ? p.get_session_id() : null;
      } catch (e) {
        return;
      }

      if (!distinctId) return;

      var signature = distinctId + '|' + (sessionId || '');
      if (!(options && options.force) && readStore(BRIDGE_KEY, null) === signature) return;

      window
        .fetch(cartRoute('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          keepalive: !!(options && options.keepalive),
          body: JSON.stringify({
            attributes: {
              _ph_distinct_id: distinctId,
              _ph_session_id: sessionId,
            },
          }),
        })
        .then(function () {
          writeStore(BRIDGE_KEY, signature);
        })
        .catch(noop);
    });
  }

  /* ------------------------------------------------------------------- cart */

  var lastCart = null;

  function fetchCart(callback) {
    if (!window.fetch) return;
    window
      .fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        lastCart = cart;
        if (callback) callback(cart);
      })
      .catch(noop);
  }

  function lineItemProperties(item) {
    if (!item) return {};
    return {
      product_id: item.product_id,
      variant_id: item.variant_id || item.id,
      product_title: item.product_title || item.title,
      variant_title: item.variant_title,
      sku: item.sku,
      price: money(item.price),
      quantity: item.quantity,
      line_value: money(item.final_line_price || item.line_price),
    };
  }

  function watchCartRequests() {
    if (!window.fetch) return;
    var originalFetch = window.fetch;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = originalFetch.apply(this, arguments);

      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        promise
          .then(function (response) {
            if (!response) return;

            if (!response.ok) {
              reportError('Cart request failed: ' + response.status, {
                error_source: 'cart',
                error_type: 'cart_request_failed',
                request_url: String(url),
                status: response.status,
              });
              return;
            }

            response
              .clone()
              .json()
              .then(function (payload) {
                handleCartResponse(url, payload);
              })
              .catch(noop);
          })
          .catch(function (error) {
            reportError(error, {
              error_source: 'cart',
              error_type: 'cart_request_network_error',
              request_url: String(url),
            });
          });
      }

      return promise;
    };
  }

  /**
   * cart_add can arrive twice — once from the fetch interceptor and once from
   * Dawn's own cartUpdate broadcast. Both paths are kept because either can
   * fail on its own (an app script replacing window.fetch kills the first; a
   * theme upgrade changing pubsub kills the second), so a short-lived
   * signature drops the duplicate rather than dropping a source.
   */
  var recentSignatures = {};

  function isDuplicate(signature, windowMs) {
    var now = Date.now();
    var last = recentSignatures[signature];

    // Opportunistic sweep so the map cannot grow unbounded in a long session.
    for (var key in recentSignatures) {
      if (now - recentSignatures[key] > 30000) delete recentSignatures[key];
    }

    recentSignatures[signature] = now;
    return !!last && now - last < (windowMs || 2000);
  }

  function captureCartAdd(item, source) {
    if (!item) return;
    var properties = lineItemProperties(item);
    if (isDuplicate('add:' + properties.variant_id + ':' + properties.quantity)) return;

    capture('cart_add', assign(properties, { add_source: ctx.template || null, capture_path: source }));
  }

  function handleCartResponse(url, payload) {
    if (!payload) return;

    if (/\/cart\/add/.test(url)) {
      var items = payload.items || [payload];
      items.forEach(function (item) {
        captureCartAdd(item, 'fetch_interceptor');
      });
      // /cart/add.js does not return cart totals, so pull them for the discount sync.
      fetchCart(function (cart) {
        syncCartDiscounts(cart, 'cart');
      });
      return;
    }

    // /cart/change.js and /cart/update.js both return the full cart.
    var previous = lastCart;
    lastCart = payload;

    if (previous && typeof payload.item_count === 'number' && payload.item_count !== previous.item_count) {
      var signature = 'count:' + payload.item_count + ':' + previous.item_count;
      if (!isDuplicate(signature)) {
        capture(payload.item_count < previous.item_count ? 'cart_quantity_decreased' : 'cart_quantity_increased', {
          cart_item_count: payload.item_count,
          cart_total: money(payload.total_price),
          item_count_delta: payload.item_count - previous.item_count,
          capture_path: 'fetch_interceptor',
        });
      }
    }

    syncCartDiscounts(payload, 'cart');
  }

  function bindCartInteractions() {
    document.addEventListener(
      'click',
      guard('cart_interaction', function (event) {
        var target = event.target;

        var removeButton = closest(target, 'cart-remove-button, .cart-remove-button');
        if (removeButton) {
          var row = closest(removeButton, 'tr, .cart-item, .cart-drawer-item');
          capture('cart_remove', {
            product_title: text(row && row.querySelector('.cart-item__name')),
            variant_title: text(row && row.querySelector('.product-option')),
            line_index: removeButton.dataset ? removeButton.dataset.index : null,
          });
          return;
        }

        var checkoutButton = closest(target, '[name="checkout"], .cart__checkout-button, a[href^="/checkout"]');
        if (checkoutButton) {
          var cart = lastCart;
          var state = discountState();
          // The pixel fires the canonical `checkout_started` when checkout
          // actually loads; this is the intent-to-checkout click before it.
          bridgeIdentityToCheckout({ force: true, keepalive: true });
          capture('checkout_button_clicked', {
            cart_total: cart ? money(cart.total_price) : ctx.cart_total,
            cart_item_count: cart ? cart.item_count : ctx.cart_item_count,
            discount_codes: state.active || [],
            checkout_source: closest(target, 'cart-drawer') ? 'cart_drawer' : ctx.template || null,
          });
          return;
        }

        if (closest(target, '#cart-icon-bubble, .header__icon--cart')) {
          capture('cart_drawer_opened', { cart_item_count: ctx.cart_item_count });
        }
      }),
      true
    );
  }

  /* --------------------------------------------------------- page-level views */

  function capturePageViews() {
    if (ctx.template === 'product' && ctx.product) {
      capture('product_viewed', {
        product_id: ctx.product.id,
        product_handle: ctx.product.handle,
        product_title: ctx.product.title,
        product_type: ctx.product.type,
        product_vendor: ctx.product.vendor,
        price: ctx.product.price,
        compare_at_price: ctx.product.compare_at_price || null,
        on_sale: !!(ctx.product.compare_at_price && ctx.product.compare_at_price > ctx.product.price),
        available: ctx.product.available,
        variant_count: ctx.product.variant_count,
        collections: ctx.product.collections,
        tags: ctx.product.tags,
      });

      setPerson({ last_product_viewed: ctx.product.handle }, { first_product_viewed: ctx.product.handle });
    }

    if (ctx.template === 'collection' && ctx.collection) {
      capture('collection_viewed', {
        collection_id: ctx.collection.id,
        collection_handle: ctx.collection.handle,
        collection_title: ctx.collection.title,
        product_count: ctx.collection.product_count,
        sort_by: ctx.collection.current_sort,
        has_active_filters: /(\?|&)filter\./.test(window.location.search),
      });
    }

    if (ctx.template === 'search' && ctx.search && ctx.search.terms) {
      capture('search_performed', {
        query: ctx.search.terms,
        results_count: ctx.search.results_count,
        has_results: ctx.search.results_count > 0,
      });
    }

    if (ctx.template === 'cart') {
      capture('cart_viewed', {
        cart_item_count: ctx.cart_item_count,
        cart_total: ctx.cart_total,
        discount_codes: ctx.cart_discount_codes || [],
      });
    }

    if (ctx.template === 'article' && ctx.article) {
      capture('article_viewed', {
        article_handle: ctx.article.handle,
        article_title: ctx.article.title,
        article_author: ctx.article.author,
      });
    }

    if (ctx.template === 'page' && ctx.page) {
      var suffix = ctx.template_suffix || '';

      if (suffix === 'cocktail-detail') {
        capture('cocktail_recipe_viewed', { cocktail_handle: ctx.page.handle, cocktail_title: ctx.page.title });
      } else if (suffix === 'stockist') {
        capture('stockist_page_viewed', {});
      } else if (suffix) {
        capture('brand_page_viewed', { page_handle: ctx.page.handle, page_title: ctx.page.title, layout: suffix });
      }
    }

    if (ctx.template === 'customers/register') {
      capture('account_registration_viewed', {});
    }

    if (ctx.template === '404') {
      capture('page_not_found', { path: window.location.pathname, referrer: document.referrer || null });
    }
  }

  /**
   * The order page is the only storefront surface that sees a completed order
   * (checkout itself is not themeable), so it is where discount codes get
   * attributed to the person profile as *redeemed* rather than merely applied.
   */
  function captureOrder() {
    var order = ctx.order;
    if (!order || !order.id) return;

    var codes = (order.discount_codes || []).map(function (entry) {
      return String(entry.code).toUpperCase();
    });

    var seen = readStore(SEEN_ORDERS_KEY, []);
    if (!Array.isArray(seen)) seen = [];
    var alreadySeen = seen.indexOf(order.id) !== -1;

    // `order_completed` belongs to the web pixel, which sees the real checkout;
    // this page is only ever a *view* of an order and must not double-count it.
    capture('order_viewed', {
      is_first_view: !alreadySeen,
      order_id: order.id,
      order_name: order.name,
      order_number: order.order_number,
      revenue: order.total,
      subtotal: order.subtotal,
      currency: order.currency,
      item_count: order.item_count,
      discount_codes: codes,
      discount_savings: order.discount_savings,
      used_discount: codes.length > 0,
      products: (order.products || []).map(function (line) {
        return line.title;
      }),
    });

    if (!alreadySeen) {
      seen.push(order.id);
      writeStore(SEEN_ORDERS_KEY, seen.slice(-50));

      if (codes.length) {
        var state = discountState();
        var redeemed = (state.redeemed || []).slice();
        codes.forEach(function (code) {
          if (redeemed.indexOf(code) === -1) redeemed.push(code);
        });
        state.redeemed = redeemed;
        writeStore(STORAGE_KEY, state);

        setPerson(
          {
            last_discount_code_redeemed: codes[codes.length - 1],
            discount_codes_redeemed: redeemed,
            discount_codes_redeemed_count: redeemed.length,
            last_order_discount_savings: order.discount_savings,
          },
          {
            first_discount_code_redeemed: codes[0],
          }
        );
      }

      setPerson(
        {
          last_order_name: order.name,
          last_order_value: order.total,
        },
        {
          first_order_name: order.name,
          first_order_value: order.total,
        }
      );
    }
  }

  /* ------------------------------------------------------- theme interactions */

  /**
   * Dawn broadcasts every successful cart mutation over its own pubsub, which
   * survives anything a third-party script does to window.fetch. This is the
   * primary path for cart_add; the fetch interceptor is the backup.
   *
   *   product-form → { source: 'product-form', cartData: <added line item> }
   *   cart-items   → { source: 'cart-items',   cartData: <full cart> }
   */
  function bindCartUpdates() {
    var subscribeFn = typeof subscribe === 'function' ? subscribe : null;
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
    if (!subscribeFn || !events || !events.cartUpdate) return;

    try {
      subscribeFn(events.cartUpdate, function (event) {
        var data = (event && event.data) || {};
        var cart = data.cartData;
        if (!cart) return;

        if (data.source === 'product-form') {
          // The add response is the line item itself, plus `items` when the
          // gift box picker adds a bundle in one request.
          var items = cart.items || [cart];
          items.forEach(function (item) {
            captureCartAdd(item, 'pubsub');
          });

          fetchCart(function (fresh) {
            syncCartDiscounts(fresh, 'cart');
          });
          return;
        }

        // Anything else hands back the whole cart, so totals are usable directly.
        if (typeof cart.item_count === 'number') {
          var previous = lastCart;
          lastCart = cart;

          if (previous && cart.item_count !== previous.item_count) {
            var signature = 'count:' + cart.item_count + ':' + previous.item_count;
            if (!isDuplicate(signature)) {
              capture(cart.item_count < previous.item_count ? 'cart_quantity_decreased' : 'cart_quantity_increased', {
                cart_item_count: cart.item_count,
                cart_total: money(cart.total_price),
                item_count_delta: cart.item_count - previous.item_count,
                capture_path: 'pubsub',
              });
            }
          }

          syncCartDiscounts(cart, 'cart');
        }
      });
    } catch (e) {
      /* no-op */
    }
  }

  function bindVariantChanges() {
    // pubsub.js / constants.js declare these in global scope (not on `window`).
    var subscribeFn = typeof subscribe === 'function' ? subscribe : null;
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
    if (!subscribeFn || !events) return;

    try {
      subscribeFn(events.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (!variant) return;
        capture('product_variant_selected', {
          product_id: ctx.product ? ctx.product.id : null,
          product_handle: ctx.product ? ctx.product.handle : null,
          variant_id: variant.id,
          variant_title: variant.title,
          price: money(variant.price),
          available: variant.available,
        });
      });
    } catch (e) {
      /* no-op */
    }
  }

  /**
   * Theme-level failures that never reach window.onerror: Dawn catches cart
   * errors and renders them inline, so without this they are invisible in
   * error tracking even though they are lost revenue.
   */
  function bindThemeErrors() {
    var subscribeFn = typeof subscribe === 'function' ? subscribe : null;
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;

    if (subscribeFn && events && events.cartError) {
      try {
        subscribeFn(events.cartError, function (event) {
          var data = (event && event.data) || {};
          reportError(data.message || data.errors || 'Cart error', {
            error_source: 'cart',
            error_type: 'cart_error',
            section_id: data.source || null,
            product_variant_id: data.productVariantId || null,
          });
        });
      } catch (e) {
        /* no-op */
      }
    }

    document.addEventListener(
      'click',
      guard('product_form_error', function (event) {
        var submit = closest(event.target, '[type="submit"], .product-form__submit');
        if (!submit) return;

        var form = closest(submit, 'form');
        if (!form) return;

        // Dawn reveals the error wrapper asynchronously after the add fails.
        window.setTimeout(function () {
          var error = form.querySelector('.product-form__error-message:not(:empty), .form__message--error');
          if (error && error.offsetParent !== null) {
            reportError(text(error), {
              error_source: 'product_form',
              error_type: 'add_to_cart_rejected',
              product_handle: ctx.product ? ctx.product.handle : null,
            });
          }
        }, 600);
      }),
      true
    );
  }

  function bindFormSubmissions() {
    document.addEventListener(
      'submit',
      guard('form_submission', function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;

        var action = form.getAttribute('action') || '';

        if (/\/contact/.test(action)) {
          var isNewsletter = !form.querySelector('[name="contact[body]"]');
          var emailField = form.querySelector('[name="contact[email]"]');
          var email = emailField && emailField.value ? emailField.value : null;

          // The email also rides on the event: with person_profiles set to
          // 'identified_only', setPersonProperties is a no-op for a visitor who
          // has never logged in, so the event is the only durable record.
          capture(isNewsletter ? 'newsletter_signup' : 'contact_form_submitted', {
            form_id: form.id || null,
            form_location: closest(form, '.footer') ? 'footer' : ctx.template || null,
            email: email,
          });

          if (isNewsletter && email) {
            setPerson({ newsletter_subscriber: true, email: email, $email: email });
          }
          return;
        }

        if (/\/account($|\/)/.test(action) && form.querySelector('[name="customer[password]"]')) {
          capture('account_created', {});
          return;
        }

        if (/\/search/.test(action)) {
          var queryField = form.querySelector('[name="q"]');
          capture('search_submitted', { query: queryField ? queryField.value : null, search_source: 'header' });
        }
      }),
      true
    );
  }

  function bindFacets() {
    document.addEventListener(
      'change',
      guard('facet_change', function (event) {
        var target = event.target;
        if (!target) return;

        if (closest(target, '#FacetSortForm, .facet-filters__sort, [data-sort-by]')) {
          capture('collection_sorted', {
            collection_handle: ctx.collection ? ctx.collection.handle : null,
            sort_by: target.value,
          });
          return;
        }

        var facet = closest(target, '.facets__form, facet-filters-form, .mobile-facets');
        if (facet && (target.type === 'checkbox' || target.type === 'radio' || target.type === 'number')) {
          capture('collection_filtered', {
            collection_handle: ctx.collection ? ctx.collection.handle : null,
            filter_name: target.name,
            filter_value: target.value,
            filter_enabled: target.type === 'checkbox' ? target.checked : true,
          });
        }
      }),
      true
    );
  }

  function bindContentInteractions() {
    document.addEventListener(
      'click',
      guard('content_interaction', function (event) {
        var target = event.target;

        var play = closest(target, '.tvc-film__play-btn, .tvc-film__video-poster, .deferred-media__poster');
        if (play) {
          var poster = closest(play, '[data-video-id]');
          capture('video_played', {
            video_id: poster && poster.dataset ? poster.dataset.videoId : null,
            video_location: ctx.template_suffix || ctx.template || null,
          });
          return;
        }

        if (closest(target, '#stockist-submit, #stockist-geo')) {
          var input = document.querySelector('.stockist__search-input, #stockist-search');
          capture('stockist_searched', {
            query: input ? input.value : null,
            used_geolocation: !!closest(target, '#stockist-geo'),
          });
          return;
        }

        var store = closest(target, '.stockist__store, .stockist-popup');
        if (store) {
          capture('stockist_store_selected', {
            store_name: text(store.querySelector('.stockist__store-name, .stockist-popup__name')),
            action: closest(target, '[href^="tel:"]') ? 'call' : closest(target, 'a') ? 'link' : 'select',
          });
          return;
        }

        var giftBoxConfirm = closest(target, '[data-modal-confirm], [data-mini-confirm]');
        if (giftBoxConfirm) {
          var picker = closest(giftBoxConfirm, 'gift-box-picker, .gift-box-picker');
          capture('gift_box_configured', {
            selection: text(picker && picker.querySelector('[data-summary-detail], [data-mini-summary-text]')),
            product_handle: ctx.product ? ctx.product.handle : null,
          });
          return;
        }

        var link = closest(target, 'a[href]');
        if (link) {
          var href = link.getAttribute('href') || '';
          if (/^mailto:/.test(href)) {
            capture('outbound_link_clicked', { link_type: 'email', link_url: href });
          } else if (/^tel:/.test(href)) {
            capture('outbound_link_clicked', { link_type: 'phone', link_url: href });
          } else if (/^https?:\/\//.test(href) && link.hostname && link.hostname !== window.location.hostname) {
            capture('outbound_link_clicked', {
              link_type: 'external',
              link_url: href,
              link_host: link.hostname,
              link_text: text(link),
            });
          }
        }
      }),
      true
    );
  }

  /* ------------------------------------------------------------------- boot */

  function init() {
    identifyCustomer();
    watchCartRequests();
    captureDiscountLanding();
    capturePageViews();
    captureOrder();
    bindVariantChanges();
    bindCartUpdates();
    bindCartInteractions();
    bindThemeErrors();
    bindFormSubmissions();
    bindFacets();
    bindContentInteractions();

    // Pick up codes applied in an earlier session / by a Shopify discount link.
    if (ctx.cart_item_count) {
      fetchCart(function (cart) {
        syncCartDiscounts(cart, 'cart_bootstrap');
      });
      bridgeIdentityToCheckout();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
